import { z } from 'zod'

/**
 * Run lifecycle. Deliberately explicit rather than a boolean `done` flag: PRD-03 RUN-002
 * requires that a caller can tell "waiting for a human" apart from "waiting for a vendor"
 * apart from "finished but only partly". Collapsing these is what makes an agent platform
 * claim success for work it did not do.
 */
export const RunStatusSchema = z.enum([
  'queued',
  'running',
  'waiting_for_approval',
  'waiting_for_input',
  /** Parked on work happening somewhere else — a remote agent, a sandbox job. */
  'waiting_for_external',
  /** Checkpointed and released by its worker; a worker will pick it up again. */
  'suspended',
  'completed',
  'partially_completed',
  'failed',
  'cancelled',
  'expired'
])
export type RunStatus = z.infer<typeof RunStatusSchema>

export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = [
  'completed',
  'partially_completed',
  'failed',
  'cancelled',
  'expired'
]

export const isTerminalRunStatus = (status: RunStatus): boolean => TERMINAL_RUN_STATUSES.includes(status)

/** Typed step kinds (PRD-03 RUN-004) so latency and failure can be sliced by what actually happened. */
export const StepTypeSchema = z.enum([
  'model_call',
  'retrieval',
  'tool_call',
  /** Work handed to a remote agent and represented locally as a child run. */
  'delegation',
  /** Parked deliberately: a schedule, a callback, a human who has not answered yet. */
  'wait',
  'policy_check',
  'approval',
  'deterministic_transform',
  'artifact_write',
  'notification'
])
export type StepType = z.infer<typeof StepTypeSchema>

export const StepStatusSchema = z.enum(['running', 'completed', 'failed', 'skipped', 'cancelled'])
export type StepStatus = z.infer<typeof StepStatusSchema>

/**
 * Operation risk drives whether a tool call can auto-execute (PRD-04 MCP-017).
 * The classification lives on the platform's tool definition, never on anything a
 * model or a remote server asserts about itself.
 */
export const RiskClassSchema = z.enum([
  'read',
  'reversible_write',
  'external_communication',
  'privileged',
  'financial',
  'destructive'
])
export type RiskClass = z.infer<typeof RiskClassSchema>

/** Risk classes that may never auto-execute regardless of caller or prompt. */
export const APPROVAL_REQUIRED_RISKS: readonly RiskClass[] = [
  'external_communication',
  'privileged',
  'financial',
  'destructive'
]

export const ApprovalDecisionSchema = z.enum(['approved', 'rejected', 'edited_and_approved', 'expired'])
export type ApprovalDecision = z.infer<typeof ApprovalDecisionSchema>

/** Capability aliases (PRD-12 MOD-001): product code never names a vendor model. */
export const ModelAliasSchema = z.enum(['fast', 'balanced', 'deep_reasoning', 'embedding'])
export type ModelAlias = z.infer<typeof ModelAliasSchema>

/**
 * Platform roles. `knowledge_steward` was retired by PRD-26 (D-11): who may maintain a knowledge
 * base is decided per base — its owner, its owning group's managers, administrators — never by a
 * role. A stored or directory-sent `knowledge_steward` no longer parses, and so confers nothing.
 */
export const RoleSchema = z.enum(['user', 'admin', 'operator', 'auditor'])
export type Role = z.infer<typeof RoleSchema>

/** Ordered from least to most privileged; used for `hasAtLeast` checks. */
export const ROLE_RANK: Record<Role, number> = {
  user: 0,
  auditor: 1,
  operator: 2,
  admin: 3
}

export const KnowledgeSourceKindSchema = z.enum(['upload', 'curated_collection', 'api'])
export type KnowledgeSourceKind = z.infer<typeof KnowledgeSourceKindSchema>

export const IngestionStatusSchema = z.enum([
  'pending',
  'extracting',
  'chunking',
  'embedding',
  'indexed',
  'failed',
  'quarantined',
  'deleted'
])
export type IngestionStatus = z.infer<typeof IngestionStatusSchema>

/**
 * Provenance labels for every context segment handed to a model (PRD-00 FND-005).
 * `retrieved_document` and `tool_result` are DATA, never instructions -- the assembler
 * marks them so the system prompt can state the boundary explicitly.
 */
export const TrustLabelSchema = z.enum([
  'platform_instruction',
  'skill_instruction',
  'user_input',
  'user_memory',
  'retrieved_document',
  'tool_result',
  'remote_agent_content'
])
export type TrustLabel = z.infer<typeof TrustLabelSchema>

export const AnswerModeSchema = z.enum(['grounded', 'hybrid', 'direct'])
export type AnswerMode = z.infer<typeof AnswerModeSchema>
